import { useCallback, useEffect, useState } from "react";
import {
  createApplication,
  getApplication,
  listApplications,
  loadDeveloper,
  rotateApplicationCredentials,
  updateApplication,
  type Application,
  type ApplicationDraft,
  type Endpoints,
  type NativeCredentials,
} from "./api";
import {
  authenticateWithPassword,
  completeLogin,
  loadSession,
  logout,
  saveSession,
  startLogin,
  type Developer,
  type Session,
} from "./auth";
import { ApplicationDetailDrawer } from "./components/ApplicationDetailDrawer";
import { ApplicationDialog } from "./components/ApplicationDialog";
import { ApplicationsView } from "./components/ApplicationsView";
import { GroupManagementView } from "./components/GroupManagementView";
import { ConsoleShell, type ConsoleView } from "./components/ConsoleShell";
import { CredentialDialog } from "./components/CredentialDialog";
import { LoginPage } from "./components/LoginPage";
import { ServerMessagesView } from "./components/ServerMessagesView";
import { PlatformSettingsView } from "./components/PlatformSettingsView";
import { QuickstartView } from "./components/QuickstartView";
import { UserManagementView } from "./components/UserManagementView";
import { SystemAccountManagementView } from "./components/SystemAccountManagementView";
import { CallbackConfigurationView } from "./components/CallbackConfigurationView";
import { CallbackFailureLogsView } from "./components/CallbackFailureLogsView";

export function App() {
  const [session, setSession] = useState<Session | null>(() => loadSession());
  const [developer, setDeveloper] = useState<Developer | null>(() => session?.user ?? null);
  const [applications, setApplications] = useState<readonly Application[]>([]);
  const [selected, setSelected] = useState<Application | null>(null);
  const [endpoints, setEndpoints] = useState<Endpoints | null>(null);
  const [view, setView] = useState<ConsoleView>("applications");
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<"create" | "edit" | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<NativeCredentials | null>(null);

  // Success and failure banners describe the latest operation and are
  // intentionally mutually exclusive. Keeping an earlier error visible after
  // a successful retry made a recovered system-account send look as if it had
  // failed again.
  const showNotice = useCallback((message: string): void => {
    setError(null);
    setNotice(message);
  }, []);
  const showError = useCallback((message: string): void => {
    setNotice(null);
    setError(message);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    const [user, apps] = await Promise.all([loadDeveloper(), listApplications()]);
    setDeveloper(user);
    setApplications(apps);
    setSelected((current) => apps.find((app) => app.appId === current?.appId) ?? apps[0] ?? null);
  }, []);

  useEffect(() => {
    let active = true;
    async function initialize(): Promise<void> {
      try {
        if (window.location.pathname === "/callback") {
          const completed = await completeLogin();
          if (!active) return;
          setSession(completed);
          history.replaceState({}, "", "/");
        } else if (loadSession() === null) {
          setLoading(false);
          return;
        }
        await refresh();
      } catch (cause) {
        if (active) setError(errorMessage(cause));
      } finally {
        if (active) setLoading(false);
      }
    }
    void initialize();
    return () => { active = false; };
  }, [refresh]);

  useEffect(() => {
    if (selected === null) return;
    let active = true;
    void getApplication(selected.appId).then((result) => {
      if (!active) return;
      setSelected(result.application);
      setEndpoints(result.endpoints);
    }).catch((cause: unknown) => {
      if (active) setError(errorMessage(cause));
    });
    return () => { active = false; };
  }, [selected?.appId]);

  async function submitApplication(draft: ApplicationDraft): Promise<void> {
    setBusy(true);
    try {
      const editing = dialog === "edit" && selected !== null;
      const result = editing ? await updateApplication(selected, draft) : await createApplication(draft);
      if (!editing) {
        const created = result as Awaited<ReturnType<typeof createApplication>>;
        if (created.credentials !== undefined) setCredentials(created.credentials);
      }
      setDialog(null);
      setSelected(result.application);
      setEndpoints(result.endpoints);
      setDrawerOpen(true);
      await refresh();
      showNotice(editing ? "应用配置已同步" : result.application.appType === "native" ? "原生应用已创建，请立即保存一次性 AppSecret" : "应用已创建，可以立即开始接入");
    } finally {
      setBusy(false);
    }
  }

  async function toggleApplication(): Promise<void> {
    if (selected === null) return;
    setBusy(true);
    try {
      const result = await updateApplication(selected, { enabled: !selected.enabled });
      setSelected(result.application);
      await refresh();
      showNotice(result.application.enabled ? "应用已启用" : "应用已停用，新 Token 将无法访问 IM");
    } catch (cause) {
      showError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function rotateCredentials(): Promise<void> {
    if (selected?.appType !== "native") return;
    const confirmed = window.confirm("轮换后旧 AppSecret 签发的 UserSig 将立即失效。确认继续？");
    if (!confirmed) return;
    setBusy(true);
    try {
      const result = await rotateApplicationCredentials(selected);
      setSelected(result.application);
      setEndpoints(result.endpoints);
      setCredentials(result.credentials);
      await refresh();
      showNotice("AppSecret 已轮换，请安全保存并更新业务服务端");
    } catch (cause) {
      showError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="loading-page"><span className="loader" /><strong>正在连接开放平台</strong><p>正在加载应用和租户配置…</p></div>;
  if (session === null && loadSession() === null) {
    return <LoginPage error={error} onEnterpriseLogin={() => { void startLogin(); }} onPasswordLogin={async (input) => {
      setError(null);
      const authenticated = await authenticateWithPassword(input);
      setSession(authenticated);
      await refresh();
    }} />;
  }

  return (
    <ConsoleShell
      developer={developer}
      navigationOpen={navigationOpen}
      view={view}
      onChangeView={setView}
      onCloseNavigation={() => { setNavigationOpen(false); }}
      onLogout={() => {
        void logout(loadSession()).then(() => {
          setSession(null);
          setDeveloper(null);
          saveSession(null);
        });
      }}
      permissions={selected?.permissions ?? []}
      onOpenNavigation={() => { setNavigationOpen(true); }}
    >
      {notice === null ? null : <Banner kind="success" message={notice} onClose={() => { setNotice(null); }} />}
      {error === null ? null : <Banner kind="danger" message={error} onClose={() => { setError(null); }} />}
      {view === "applications" ? <ApplicationsView applications={applications} onCreate={() => { setDialog("create"); }} onSelect={(application) => { setSelected(application); setDrawerOpen(true); }} /> : null}
      {view === "users" ? <UserManagementView key={selected?.appId ?? "none"} application={selected} applications={applications} onError={showError} onNotice={showNotice} onSelect={setSelected} /> : null}
      {view === "system-accounts" ? <SystemAccountManagementView key={selected?.appId ?? "none"} application={selected} applications={applications} onError={showError} onNotice={showNotice} onSelect={setSelected} /> : null}
      {view === "groups" ? <GroupManagementView key={selected?.appId ?? "none"} application={selected} applications={applications} onError={showError} onNotice={showNotice} onSelect={setSelected} /> : null}
      {view === "server-messages" ? <ServerMessagesView key={selected?.appId ?? "none"} application={selected} applications={applications} onSelect={setSelected} /> : null}
      {view === "callbacks" ? <CallbackConfigurationView key={selected?.appId ?? "none"} application={selected} applications={applications} onError={showError} onNotice={showNotice} onSelect={setSelected} /> : null}
      {view === "callback-failures" ? <CallbackFailureLogsView key={selected?.appId ?? "none"} application={selected} applications={applications} onSelect={setSelected} /> : null}
      {view === "quickstart" ? <QuickstartView application={selected} applications={applications} endpoints={endpoints} onManageApplications={() => { setView("applications"); }} onSelect={setSelected} /> : null}
      {view === "settings" ? <PlatformSettingsView endpoints={endpoints} /> : null}
      {drawerOpen && selected !== null ? <ApplicationDetailDrawer application={selected} busy={busy} endpoints={endpoints} onClose={() => { setDrawerOpen(false); }} onEdit={() => { setDialog("edit"); }} onQuickstart={() => { setDrawerOpen(false); setView("quickstart"); }} onRotate={() => { void rotateCredentials(); }} onToggle={() => { void toggleApplication(); }} /> : null}
      {dialog !== null ? <ApplicationDialog application={dialog === "edit" ? selected ?? undefined : undefined} busy={busy} onClose={() => { setDialog(null); }} onSubmit={submitApplication} /> : null}
      {credentials === null ? null : <CredentialDialog credentials={credentials} onClose={() => { setCredentials(null); }} />}
    </ConsoleShell>
  );
}

function Banner({ kind, message, onClose }: { readonly kind: "success" | "danger"; readonly message: string; readonly onClose: () => void }) {
  return <div className={`notice notice-${kind}`} role={kind === "danger" ? "alert" : "status"}><span>{message}</span><button aria-label="关闭提示" type="button" onClick={onClose}>×</button></div>;
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}
